import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/payments/products").then((r) => setProducts(r.data.products));
  }, []);

  const buy = async (p) => {
    if (!user) { nav("/login"); return; }
    setBusy(p.id);
    try {
      const r = await api.post("/payments/checkout", { product_id: p.id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const isCoach = user?.role === "coach";

  // Show team plan tile only for coaches (or logged-out with a hint)
  const filtered = products.filter((p) => {
    if (p.id === "coach_team_monthly") return isCoach || !user;
    if (isCoach) return p.id === "coach_team_monthly";
    return true;
  });

  const tiles = isCoach
    ? filtered.map((p) => ({
        id: p.id, name: p.name,
        price: `$${(p.amount_cents / 100).toFixed(2)}/mo`,
        desc: "Coach up to 25 athletes. Read-only access to their plans.",
        cta: p,
      }))
    : [
        { id: "free", name: "Free", price: "$0", desc: "3 analyses / month. Resets monthly.", cta: null },
        ...filtered.map((p) => ({
          id: p.id, name: p.name,
          price: `$${(p.amount_cents / 100).toFixed(2)}${p.type === "subscription" ? "/mo" : ""}`,
          desc: p.type === "credits" ? `Adds ${p.credits} analyses. Never expires.` :
                p.id === "coach_team_monthly" ? "For coaches: up to 25 athletes." :
                "Unlimited analyses every month.",
          cta: p,
        })),
      ];

  return (
    <div>
      <div className="rs-underline mb-3" />
      <h1 className="font-display-heavy text-4xl">PRICING</h1>
      <p className="text-neutral-400 mt-2 max-w-lg">Start free. Add credits when you need more, or go unlimited.</p>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <div key={t.id} className={`border rounded-md p-6 flex flex-col ${t.id === "unlimited_monthly" ? "border-red-600 bg-neutral-900" : "border-neutral-800 bg-neutral-900"}`}>
            {t.id === "unlimited_monthly" && <div className="text-xs uppercase text-red-500 font-display tracking-widest mb-2">Best value</div>}
            <div className="font-display-heavy text-2xl">{t.name}</div>
            <div className="font-display-heavy text-4xl mt-3">{t.price}</div>
            <p className="text-neutral-400 mt-2 flex-1">{t.desc}</p>
            {t.cta ? (
              <Button data-testid={`buy-${t.id}`} disabled={busy === t.id} onClick={() => buy(t.cta)} className="mt-6 h-12 bg-red-600 hover:bg-red-700 font-semibold">
                {busy === t.id ? "Loading…" : (t.cta.type === "subscription" ? "Subscribe" : "Buy pack")}
              </Button>
            ) : (
              <Button data-testid="buy-free" onClick={() => nav(user ? "/upload" : "/register")} variant="outline" className="mt-6 h-12 border-neutral-700 text-white hover:bg-neutral-800">
                {user ? "Use free tier" : "Start free"}
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-neutral-500 text-xs mt-6">Test mode. Use card 4242 4242 4242 4242 with any future date and any CVC.</p>
    </div>
  );
}
